import { IconBox, IconTool } from "@tabler/icons-react";
import ProductServiceItemForm from "./ProductServiceItemForm";
import { Box, Button, Flex, Title, Text, Paper } from "@mantine/core";

interface ProductsServicesTabContentProps {
  isViewMode?: boolean;
  companyId?: string; // Kept for compatibility if needed, but unused for fetching now
  products: any[];
  services: any[];
  onAddProduct: () => void;
  onUpdateProduct: (index: number, field: string, value: any) => void;
  onRemoveProduct: (index: number) => void;
  onAddService: () => void;
  onUpdateService: (index: number, field: string, value: any) => void;
  onRemoveService: (index: number) => void;
  productErrors: Record<number, { name?: string; description?: string }>;
  serviceErrors: Record<number, { name?: string; description?: string }>;
}

const ProductsServicesTabContent = ({
  isViewMode = false,
  products,
  services,
  onAddProduct,
  onUpdateProduct,
  onRemoveProduct,
  onAddService,
  onUpdateService,
  onRemoveService,
  productErrors,
  serviceErrors,
}: ProductsServicesTabContentProps) => {
  const renderEmptySection = (
    description: string,
    buttonLabel: string,
    emptyMessage: string,
    onAdd: () => void,
    icon?: React.ReactNode
  ) => {
    return (
      <Box mb="xl">
        <Flex justify="space-between" align="flex-start" mb="md">
          <Box>
            <Title order={4} mb={4} fw={400}>
              {buttonLabel === "Add Product" ? "Products" : "Services"}
            </Title>
            <Text fz="sm" c="slate.6">
              {description}
            </Text>
          </Box>
          {!isViewMode && (
            <Button leftSection={icon} onClick={onAdd} variant="filled" color="primary">
              {buttonLabel}
            </Button>
          )}
        </Flex>

        <Paper
          p={0}
          radius="md"
          h={300}
          shadow="none"
          style={{
            border: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}
        >
          <Text c="slate.6" fz="lg">
            {emptyMessage}
          </Text>
        </Paper>
      </Box>
    );
  };

  return (
    <>
      {/* Products Section */}
      <Box mb="xl">
        {products.length === 0 ? (
          renderEmptySection(
            "Add your physical products with descriptions.",
            "Add Product",
            'No products added. Click "Add Product"',
            onAddProduct,
            <IconBox size={20} />
          )
        ) : (
          <>
            <Flex justify="space-between" align="flex-start" mb="md">
              <Box>
                <Title order={4} mb={4} fw={500}>
                  Products
                </Title>
                <Text fz="sm" c="slate.6">
                  Add your physical products with descriptions.
                </Text>
              </Box>
              {!isViewMode && (
                <Button leftSection={<IconBox size={20} />} onClick={onAddProduct} variant="filled" color="primary">
                  Add Product
                </Button>
              )}
            </Flex>
            {products.map((item, index) => (
              <ProductServiceItemForm
                key={item.id || item.clientId || index}
                index={index}
                type="product"
                data={item}
                onChange={(field, value) => onUpdateProduct(index, field, value)}
                onRemove={() => onRemoveProduct(index)}
                isViewMode={isViewMode}
                errors={productErrors[index]}
              />
            ))}
          </>
        )}
      </Box>

      {/* Services Section */}
      <Box mb="xl">
        {services.length === 0 ? (
          renderEmptySection(
            "Add your manufacturing services with descriptions.",
            "Add Service",
            'No services added. Click "Add Service"',
            onAddService,
            <IconTool size={20} />
          )
        ) : (
          <>
            <Flex justify="space-between" align="flex-start" mb="md">
              <Box>
                <Title order={4} mb={4} fw={500}>
                  Services
                </Title>
                <Text fz="sm" c="slate.6">
                  Add your manufacturing services with descriptions.
                </Text>
              </Box>
              {!isViewMode && (
                <Button leftSection={<IconTool size={20} />} onClick={onAddService} variant="filled" color="primary">
                  Add Service
                </Button>
              )}
            </Flex>
            {services.map((item, index) => (
              <ProductServiceItemForm
                key={item.id || item.clientId || index}
                index={index}
                type="service"
                data={item}
                onChange={(field, value) => onUpdateService(index, field, value)}
                onRemove={() => onRemoveService(index)}
                isViewMode={isViewMode}
                errors={serviceErrors[index]}
              />
            ))}
          </>
        )}
      </Box>
    </>
  );
};

export default ProductsServicesTabContent;
